/**
 * @fileoverview How to Play page with comprehensive instructions
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import { ArrowRight, Play, Users, Settings, Share2, Gamepad2, List, Shuffle, Save, Upload, Dice5, Palette, BookOpen, Heart, Brain, Target, Globe, Lightbulb, CheckCircle } from 'lucide-react';
import { HelpPageLayout } from '../../components/help/HelpPageLayout';

const HowToPlay: React.FC = () => {
  const { t } = useTranslation();

  const gameTypes = [
    {
      name: "Trivia",
      description: "Engaging quiz games that test and expand knowledge",
      blogUrl: "https://spinnergames.fun/blogs/trivia/",
      examples: [
        "History: Who was the first president of the United States?",
        "Science: What is the chemical symbol for gold?",
        "Entertainment: Who played Iron Man in the Marvel movies?"
      ]
    },
    {
      name: "Truth or Dare",
      description: "Classic party game that encourages openness and fun challenges",
      blogUrl: "https://spinnergames.fun/blogs/truth-or-dare",
      examples: [
        "Truth: What's your biggest fear?",
        "Dare: Sing a song in a funny voice",
        "Truth: What's your most embarrassing moment?"
      ]
    },
    {
      name: "Which One Are You?",
      description: "Personality-based games that help you discover more about yourself and others",
      blogUrl: "https://spinnergames.fun/blogs/which-one-are-you",
      examples: [
        "What's your chocolate bar style?",
        "- Break it into pieces. OR Bite directly into it.",
        "What's your favorite way to eat a pizza?",
        "- Fold it in half. OR Eat it like a pie."
      ]
    },
    {
      name: "Would You Rather",
      description: "Decision-making games that spark interesting conversations",
      blogUrl: "https://spinnergames.fun/blogs/would-you-rather",
      examples: [
        "Would you rather have unlimited money or unlimited time?",
        "Would you rather be invisible or be able to fly?",
        "Would you rather travel to the past or the future?"
      ]
    }
  ];

  const miscellaneousOptions = [
    {
      icon: <Dice5 className="w-6 h-6" />,
      title: "Dice",
      description: "A fun way to roll the dice for unlimited dice board games like Ludo, Snakes and Ladders, Monopoly, or toddler custom learning games etc."
    },
    {
      icon: <List className="w-6 h-6" />,
      title: "Numbers",
      description: "Mix luck and Math, or just do activities on winning Numbers or a group of numbers"
    },
    {
      icon: <Shuffle className="w-6 h-6" />,
      title: "Random Numbers",
      description: "Create Math variation, learn with fun."
    },
    {
      icon: <Palette className="w-6 h-6" />,
      title: "Colors",
      description: "Play with the Colors, learning colors, or activities for the younger ones."
    },
    {
      icon: <Settings className="w-6 h-6" />,
      title: "Binary Choices",
      description: "Help you to make a decision - Yes or No, toss the coin."
    }
  ];

  return (
    <HelpPageLayout>
      <Helmet>
        <title>How to Play - Spinner Games !!</title>
        <meta name="description" content="Learn how to use our interactive spinner app. Step-by-step guide for Would You Rather, Truth or Dare, Trivia, and custom spinners." />
                  <meta property="og:title" content="How to Play - Spinner Games !!" />
        <meta property="og:description" content="Learn how to use our interactive spinner app with step-by-step instructions." />
        <link rel="canonical" href="https://spinnergames.fun/help/how-to-play" />
        <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "HowTo",
          "name": "How to Play Spinner Games !!",
          "description": "Step-by-step guide to using the interactive spinner app"
        })}
        </script>
      </Helmet>

             <div className="max-w-4xl mx-auto px-4 py-6">
         <div className="text-center mb-8">
           <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-3">
             How to Play Spinner Games !!
           </h1>
           <p className="text-xl text-gray-600 dark:text-gray-300">
             Get together for fun experience
           </p>
         </div>



                 {/* Game Rules */}
         <div className="mb-12">
           <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-6 text-center">
             Game Rules
           </h2>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8">
            <p className="text-gray-600 dark:text-gray-300 mb-6 leading-relaxed">
              This game is designed for friends and family to bring people together and have a good time. 
              This game is curated carefully with all ages questionnaires and tasks, avoiding any inappropriate 
              content and doesn't have anything that may hurt religious or cultural beliefs.
            </p>
            <p className="text-gray-600 dark:text-gray-300 mb-6 leading-relaxed">
              This game is targeted to create fun with near and dear ones, we do not encourage or promote any 
              liquor, harm, insulting behavior, or activities that could be dangerous or inappropriate. 
              All content is family-friendly and suitable for players of all ages.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mt-8">
              <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg">
                <h4 className="font-semibold text-green-800 dark:text-green-200 mb-2">✅ What's Allowed:</h4>
                <ul className="text-sm text-green-700 dark:text-green-300 space-y-1">
                  <li>• Family-friendly questions and challenges</li>
                  <li>• Fun, light-hearted activities</li>
                  <li>• Educational trivia questions</li>
                  <li>• Creative decision-making scenarios</li>
                  <li>• Safe, indoor activities</li>
                </ul>
              </div>
              <div className="bg-red-50 dark:bg-red-900/20 p-4 rounded-lg">
                <h4 className="font-semibold text-red-800 dark:text-red-200 mb-2">❌ What's Not Allowed:</h4>
                <ul className="text-sm text-red-700 dark:text-red-300 space-y-1">
                  <li>• Inappropriate or adult content</li>
                  <li>• Dangerous or risky activities</li>
                  <li>• Offensive or discriminatory content</li>
                  <li>• Alcohol or substance-related challenges</li>
                  <li>• Activities that could cause harm</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

                 {/* Video Tutorial */}
         <div className="mb-12">
           <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-6 text-center">
             Video Tutorial
           </h2>
           <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
             <div className="flex items-start space-x-4">
               <div className="flex-shrink-0">
                 <div className="w-12 h-12 bg-red-100 dark:bg-red-900 rounded-full flex items-center justify-center text-red-600 dark:text-red-300">
                   <Play className="w-6 h-6" />
                 </div>
               </div>
               <div className="flex-1">
                 <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                   Watch How to Play
                 </h3>
                 <p className="text-gray-600 dark:text-gray-300 mb-6">
                   Learn how to use Spinner Games with this step-by-step video tutorial.
                 </p>
                 
                 {/* YouTube Video Embed */}
                 <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
                   <iframe
                     className="absolute top-0 left-0 w-full h-full rounded-lg"
                     src="https://www.youtube.com/embed/HZcMRx72oEM"
                     title="How to Play Spinner Games Tutorial"
                     frameBorder="0"
                     allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                     allowFullScreen
                   ></iframe>
                 </div>
                 
                 <div className="mt-4 text-center">
                   <a
                     href="https://youtu.be/HZcMRx72oEM"
                     target="_blank"
                     rel="noopener noreferrer"
                     className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors"
                   >
                     <Play className="w-4 h-4 mr-2" />
                     Watch on YouTube
                   </a>
                 </div>
               </div>
             </div>
           </div>
         </div>

                 {/* Managing Participants List */}
         <div className="mb-12">
           <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-6 text-center">
             Managing Participants List
           </h2>
          
          <div className="space-y-8">
            {/* Add Participants */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-green-100 dark:bg-green-900 rounded-full flex items-center justify-center text-green-600 dark:text-green-300">
                    <Users className="w-6 h-6" />
                  </div>
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                    Add Participants
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300 mb-4">
                    There are two ways to add participants:
                  </p>
                  
                  <div className="space-y-6">
                    <div>
                      <h4 className="font-medium text-gray-900 dark:text-white mb-2">1. Directly adding and removing from the list</h4>
                      <ul className="space-y-2 text-gray-600 dark:text-gray-300">
                        <li className="flex items-center">
                          <ArrowRight className="w-4 h-4 mr-2 text-blue-500" />
                          Enter the names of people or options you want to include in your spinner
                        </li>
                        <li className="flex items-center">
                          <ArrowRight className="w-4 h-4 mr-2 text-blue-500" />
                          Type in names in the participants section
                        </li>
                        <li className="flex items-center">
                          <ArrowRight className="w-4 h-4 mr-2 text-blue-500" />
                          Use the + button to add more people
                        </li>
                        <li className="flex items-center">
                          <ArrowRight className="w-4 h-4 mr-2 text-blue-500" />
                          Remove participants with the (-) icon in front of the participant
                        </li>
                        <li className="flex items-center">
                          <ArrowRight className="w-4 h-4 mr-2 text-blue-500" />
                          Sort or Shuffle names for random order
                        </li>
                      </ul>
                    </div>
                    
                    <div>
                      <h4 className="font-medium text-gray-900 dark:text-white mb-2">2. Use the Load button</h4>
                      <ul className="space-y-2 text-gray-600 dark:text-gray-300">
                        <li className="flex items-center">
                          <ArrowRight className="w-4 h-4 mr-2 text-blue-500" />
                          Open Load Participants
                        </li>
                        <li className="flex items-center">
                          <ArrowRight className="w-4 h-4 mr-2 text-blue-500" />
                          Choose clear and load or append it
                        </li>
                        <li className="flex items-center">
                          <ArrowRight className="w-4 h-4 mr-2 text-blue-500" />
                          Browse if you have a CSV file or
                        </li>
                        <li className="flex items-center">
                          <ArrowRight className="w-4 h-4 mr-2 text-blue-500" />
                          Provide comma-separated list in the text input to load
                        </li>
                        <li className="flex items-center">
                          <ArrowRight className="w-4 h-4 mr-2 text-blue-500" />
                          Additionally, use the Clear button to clear the list and Save Participants in cache or in profile if logged in
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

                 {/* Fun Modes */}
         <div className="mb-12">
           <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-6 text-center">
             Fun Modes - Choose Your Game
           </h2>
           <p className="text-center text-gray-600 dark:text-gray-300 mb-6">
             Select an option from the following game types:
           </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                         {gameTypes.map((game, index) => (
               <div key={index} className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
                 <div className="flex items-center justify-between mb-3">
                   <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                     {game.name}
                   </h3>
                                       <a
                      href={game.blogUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center px-3 py-1 text-xs font-medium text-white bg-blue-600 dark:bg-blue-500 rounded-full hover:bg-blue-700 dark:hover:bg-blue-600 transition-colors"
                    >
                      Blog
                    </a>
                 </div>
                <p className="text-gray-600 dark:text-gray-300 mb-4">
                  {game.description}
                </p>
                                 <div className="space-y-2">
                   <h4 className="font-medium text-gray-900 dark:text-white">Examples:</h4>
                   <div className="space-y-1">
                     {game.examples.map((example, exampleIndex) => (
                       <div key={exampleIndex} className="text-sm text-gray-600 dark:text-gray-300">
                         {example.startsWith('-') ? (
                           <span className="ml-4">{example}</span>
                         ) : (
                           <span>• {example}</span>
                         )}
                       </div>
                     ))}
                   </div>
                 </div>
              </div>
            ))}
          </div>
          
          {/* Just Spinner */}
          <div className="mt-8 bg-green-50 dark:bg-green-900/20 rounded-lg p-6">
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
              Just Spinner: A Custom Spinner
            </h3>
            <p className="text-gray-600 dark:text-gray-300 mb-4">
              Run your imagination and create your games using your favorite spinner for learn and fun.
            </p>
          </div>
          
                                {/* Miscellaneous Options */}
            <div className="mt-8 bg-blue-50 dark:bg-blue-900/20 rounded-lg p-6">
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                Miscellaneous Options with Just Spinner for More Fun
              </h3>
              <p className="text-gray-600 dark:text-gray-300 mb-6">
                Possibilities are unlimited; check our{' '}
                                 <a
                   href="https://spinnergames.fun/blogs"
                   target="_blank"
                   rel="noopener noreferrer"
                   className="inline-flex items-center px-3 py-1 text-xs font-medium text-white bg-blue-600 dark:bg-blue-500 rounded-full hover:bg-blue-700 dark:hover:bg-blue-600 transition-colors"
                 >
                   blog
                 </a>
                {' '}sections for more ideas.
              </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {miscellaneousOptions.map((option, index) => (
                <div key={index} className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow-md">
                  <div className="flex items-center space-x-3 mb-3">
                    <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center text-blue-600 dark:text-blue-300">
                      {option.icon}
                    </div>
                    <h4 className="font-semibold text-gray-900 dark:text-white">{option.title}</h4>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-300">{option.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

                 {/* Spinner Styles */}
         <div className="mb-12">
           <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-6 text-center">
             Spinner Styles
           </h2>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-purple-100 dark:bg-purple-900 rounded-full flex items-center justify-center text-purple-600 dark:text-purple-300">
                  <Palette className="w-6 h-6" />
                </div>
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                  Choose Your Favorite Spinner
                </h3>
                <p className="text-gray-600 dark:text-gray-300 mb-4">
                  Choose your favorite Spinner from the list for the game or fun activities.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-medium text-gray-900 dark:text-white mb-3">Family-Friendly Options:</h4>
                    <ul className="space-y-2 text-gray-600 dark:text-gray-300">
                      <li className="flex items-center">
                        <span className="w-2 h-2 bg-blue-500 rounded-full mr-3"></span>
                        Cola bottle
                      </li>
                      <li className="flex items-center">
                        <span className="w-2 h-2 bg-blue-500 rounded-full mr-3"></span>
                        Orange Soda Bottle
                      </li>
                      <li className="flex items-center">
                        <span className="w-2 h-2 bg-blue-500 rounded-full mr-3"></span>
                        Water Bottle
                      </li>
                      <li className="flex items-center">
                        <span className="w-2 h-2 bg-blue-500 rounded-full mr-3"></span>
                        Wheel
                      </li>
                      <li className="flex items-center">
                        <span className="w-2 h-2 bg-blue-500 rounded-full mr-3"></span>
                        Compass
                      </li>
                      <li className="flex items-center">
                        <span className="w-2 h-2 bg-blue-500 rounded-full mr-3"></span>
                        Arrow
                      </li>
                      <li className="flex items-center">
                        <span className="w-2 h-2 bg-blue-500 rounded-full mr-3"></span>
                        Knife
                      </li>
                      <li className="flex items-center">
                        <span className="w-2 h-2 bg-blue-500 rounded-full mr-3"></span>
                        Magic Wand
                      </li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-900 dark:text-white mb-3">Adults' Options:</h4>
                    <ul className="space-y-2 text-gray-600 dark:text-gray-300">
                      <li className="flex items-center">
                        <span className="w-2 h-2 bg-orange-500 rounded-full mr-3"></span>
                        Beer Bottle
                      </li>
                      <li className="flex items-center">
                        <span className="w-2 h-2 bg-orange-500 rounded-full mr-3"></span>
                        Whisky Bottle
                      </li>
                      <li className="flex items-center">
                        <span className="w-2 h-2 bg-orange-500 rounded-full mr-3"></span>
                        Champagne Bottle
                      </li>
                    </ul>
                    <div className="mt-4 p-3 bg-orange-50 dark:bg-orange-900/20 rounded-lg">
                      <p className="text-sm text-orange-700 dark:text-orange-300">
                        <strong>Note:</strong> Adult options are available for users of legal drinking age.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

                 {/* Share Section */}
         <div className="mb-12 bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 rounded-lg p-6">
          <div className="text-center">
            <div className="w-16 h-16 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center mx-auto mb-6">
              <Share2 className="w-8 h-8 text-blue-600 dark:text-blue-300" />
            </div>
            <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
              Share the Fun
            </h3>
                         <p className="text-lg text-gray-600 dark:text-gray-300">
               Share your spinner with participants' lists to family and friends or on your favorite social media.
             </p>
          </div>
        </div>

        {/* Key Benefits Section */}
        <div className="mb-12">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-6 text-center">
            Key Benefits
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Educational Value */}
            <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-6 border border-blue-200 dark:border-blue-700">
              <div className="flex items-center space-x-4 mb-4">
                <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center text-blue-600 dark:text-blue-300">
                  <BookOpen className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                  Educational Value
                </h3>
              </div>
              <ul className="space-y-3">
                <li className="flex items-start space-x-3">
                  <CheckCircle className="w-5 h-5 text-green-500 dark:text-green-400 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700 dark:text-gray-300 text-sm">
                    <strong>Knowledge Enhancement:</strong> Trivia games expand general knowledge and critical thinking
                  </span>
                </li>
                <li className="flex items-start space-x-3">
                  <CheckCircle className="w-5 h-5 text-green-500 dark:text-green-400 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700 dark:text-gray-300 text-sm">
                    <strong>Learning Through Play:</strong> Interactive education that makes learning enjoyable
                  </span>
                </li>
                <li className="flex items-start space-x-3">
                  <CheckCircle className="w-5 h-5 text-green-500 dark:text-green-400 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700 dark:text-gray-300 text-sm">
                    <strong>Skill Development:</strong> Improves memory, reasoning, and problem-solving abilities
                  </span>
                </li>
              </ul>
            </div>

            {/* Social Development */}
            <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-6 border border-green-200 dark:border-green-700">
              <div className="flex items-center space-x-4 mb-4">
                <div className="w-12 h-12 bg-green-100 dark:bg-green-900 rounded-full flex items-center justify-center text-green-600 dark:text-green-300">
                  <Users className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                  Social Development
                </h3>
              </div>
              <ul className="space-y-3">
                <li className="flex items-start space-x-3">
                  <CheckCircle className="w-5 h-5 text-green-500 dark:text-green-400 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700 dark:text-gray-300 text-sm">
                    <strong>Relationship Building:</strong> Strengthens bonds between family members and friends
                  </span>
                </li>
                <li className="flex items-start space-x-3">
                  <CheckCircle className="w-5 h-5 text-green-500 dark:text-green-400 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700 dark:text-gray-300 text-sm">
                    <strong>Communication Skills:</strong> Encourages open dialogue and active listening
                  </span>
                </li>
                <li className="flex items-start space-x-3">
                  <CheckCircle className="w-5 h-5 text-green-500 dark:text-green-400 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700 dark:text-gray-300 text-sm">
                    <strong>Empathy Development:</strong> Understanding different perspectives through interactive games
                  </span>
                </li>
              </ul>
            </div>

            {/* Digital Detox & Connection */}
            <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-6 border border-purple-200 dark:border-purple-700">
              <div className="flex items-center space-x-4 mb-4">
                <div className="w-12 h-12 bg-purple-100 dark:bg-purple-900 rounded-full flex items-center justify-center text-purple-600 dark:text-purple-300">
                  <Globe className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                  Digital Detox & Connection
                </h3>
              </div>
              <ul className="space-y-3">
                <li className="flex items-start space-x-3">
                  <CheckCircle className="w-5 h-5 text-green-500 dark:text-green-400 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700 dark:text-gray-300 text-sm">
                    <strong>Screen-Free Interaction:</strong> Promotes real-world social engagement
                  </span>
                </li>
                <li className="flex items-start space-x-3">
                  <CheckCircle className="w-5 h-5 text-green-500 dark:text-green-400 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700 dark:text-gray-300 text-sm">
                    <strong>Quality Time:</strong> Encourages meaningful face-to-face conversations
                  </span>
                </li>
                <li className="flex items-start space-x-3">
                  <CheckCircle className="w-5 h-5 text-green-500 dark:text-green-400 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700 dark:text-gray-300 text-sm">
                    <strong>Community Building:</strong> Fosters a sense of belonging and togetherness
                  </span>
                </li>
              </ul>
            </div>

            {/* Emotional Intelligence */}
            <div className="bg-pink-50 dark:bg-pink-900/20 rounded-lg p-6 border border-pink-200 dark:border-pink-700">
              <div className="flex items-center space-x-4 mb-4">
                <div className="w-12 h-12 bg-pink-100 dark:bg-pink-900 rounded-full flex items-center justify-center text-pink-600 dark:text-pink-300">
                  <Heart className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                  Emotional Intelligence
                </h3>
              </div>
              <ul className="space-y-3">
                <li className="flex items-start space-x-3">
                  <CheckCircle className="w-5 h-5 text-green-500 dark:text-green-400 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700 dark:text-gray-300 text-sm">
                    <strong>Tolerance & Patience:</strong> Develops skills to handle diverse personalities and situations
                  </span>
                </li>
                <li className="flex items-start space-x-3">
                  <CheckCircle className="w-5 h-5 text-green-500 dark:text-green-400 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700 dark:text-gray-300 text-sm">
                    <strong>Conflict Resolution:</strong> Practice navigating disagreements in a safe environment
                  </span>
                </li>
                <li className="flex items-start space-x-3">
                  <CheckCircle className="w-5 h-5 text-green-500 dark:text-green-400 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700 dark:text-gray-300 text-sm">
                    <strong>Adaptability:</strong> Learn to work with different people and adapt to various social contexts
                  </span>
                </li>
              </ul>
            </div>

            {/* Decision-Making Skills */}
            <div className="bg-orange-50 dark:bg-orange-900/20 rounded-lg p-6 border border-orange-200 dark:border-orange-700">
              <div className="flex items-center space-x-4 mb-4">
                <div className="w-12 h-12 bg-orange-100 dark:bg-orange-900 rounded-full flex items-center justify-center text-orange-600 dark:text-orange-300">
                  <Target className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                  Decision-Making Skills
                </h3>
              </div>
              <ul className="space-y-3">
                <li className="flex items-start space-x-3">
                  <CheckCircle className="w-5 h-5 text-green-500 dark:text-green-400 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700 dark:text-gray-300 text-sm">
                    <strong>Critical Thinking:</strong> Practice making informed choices
                  </span>
                </li>
                <li className="flex items-start space-x-3">
                  <CheckCircle className="w-5 h-5 text-green-500 dark:text-green-400 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700 dark:text-gray-300 text-sm">
                    <strong>Consensus Building:</strong> Learn to work together toward common goals
                  </span>
                </li>
                <li className="flex items-start space-x-3">
                  <CheckCircle className="w-5 h-5 text-green-500 dark:text-green-400 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700 dark:text-gray-300 text-sm">
                    <strong>Leadership Development:</strong> Opportunities to take initiative and guide group activities
                  </span>
                </li>
              </ul>
            </div>

            {/* Why It Matters Today */}
            <div className="bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 rounded-lg p-6 border border-gray-200 dark:border-gray-700 md:col-span-2 lg:col-span-1">
              <div className="flex items-center space-x-4 mb-4">
                <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center text-blue-600 dark:text-blue-300">
                  <Lightbulb className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                  Why It Matters Today
                </h3>
              </div>
              <p className="text-gray-700 dark:text-gray-300 text-sm mb-4">
                In our increasingly digital world, genuine human connection has become more valuable than ever.
              </p>
              <ul className="space-y-2">
                <li className="flex items-center space-x-2">
                  <Globe className="w-4 h-4 text-blue-500" />
                  <span className="text-gray-700 dark:text-gray-300 text-sm">Real Social Interaction</span>
                </li>
                <li className="flex items-center space-x-2">
                  <Users className="w-4 h-4 text-green-500" />
                  <span className="text-gray-700 dark:text-gray-300 text-sm">Intergenerational Bonding</span>
                </li>
                <li className="flex items-center space-x-2">
                  <Brain className="w-4 h-4 text-purple-500" />
                  <span className="text-gray-700 dark:text-gray-300 text-sm">Skill Development</span>
                </li>
                <li className="flex items-center space-x-2">
                  <Heart className="w-4 h-4 text-pink-500" />
                  <span className="text-gray-700 dark:text-gray-300 text-sm">Mental Health</span>
                </li>
              </ul>
            </div>
          </div>
        </div>

                 {/* CTA */}
         <div className="text-center mt-8">
          <a
            href="/"
            className="inline-flex items-center px-6 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors"
          >
            Start Playing Now
            <ArrowRight className="w-5 h-5 ml-2" />
          </a>
        </div>
      </div>
    </HelpPageLayout>
  );
};

export default HowToPlay; 